import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Role } from './api';
import { HELP, type HelpSection, type HelpWhere } from './helpContent';

/**
 * The Help manual (owner 10/24). Built into the app so it is updated with each app update.
 * Each person sees only the parts for their role (and for where they are: desktop or phone);
 * the Help link opens the section for the page they were on. Content lives in helpContent.ts.
 */

/** Which sections this person sees here. */
export function helpFor(role: Role, where: HelpWhere, hasEmployee: boolean): HelpSection[] {
  return HELP.filter((s) => s.where.includes(where) && (s.who.includes(role)
    // managers, leads and admins on an employee list also use the employee parts of the phone app
    || (where === 'phone' && hasEmployee && s.who.includes('employee'))));
}

/** The first section listed for a page key ("day", "phone:today"…), if this person can see it. */
export function startFor(sections: HelpSection[], page: string): string | undefined {
  return sections.find((s) => s.pages?.includes(page))?.id;
}

export function HelpPage({ role, where, hasEmployee, start, onClose }: {
  role: Role; where: HelpWhere; hasEmployee: boolean; start?: string; onClose?: () => void;
}) {
  const all = useMemo(() => helpFor(role, where, hasEmployee), [role, where, hasEmployee]);
  const [q, setQ] = useState('');
  const [openList, setOpenList] = useState(false);
  const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const shown = words.length ? all.filter((s) => { const t = (s.title + ' ' + s.group + ' ' + s.body).toLowerCase(); return words.every((w) => t.includes(w)); }) : all;
  const groups = useMemo(() => {
    const m = new Map<string, HelpSection[]>();
    for (const s of shown) m.set(s.group, [...(m.get(s.group) ?? []), s]);
    return [...m.entries()];
  }, [shown]);
  const bodyRef = useRef<HTMLDivElement>(null);
  const jump = (id: string) => {
    setOpenList(false);
    const el = document.getElementById('help-' + id);
    if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); el.classList.remove('hflash'); void el.offsetWidth; el.classList.add('hflash'); }
  };
  useEffect(() => {
    if (!start) return;
    const t = setTimeout(() => { document.getElementById('help-' + start)?.scrollIntoView({ block: 'start' }); }, 30);
    return () => clearTimeout(t);
  }, [start]);
  const phone = where === 'phone';
  const titleOf = (id: string) => all.find((s) => s.id === id)?.title;

  return (
    <div className={'helpwrap' + (phone ? ' hphone' : '')}>
      <aside className="helpside noprint" aria-label="Help contents">
        <div className="helphead">
          <div style={{ fontSize: phone ? 18 : 20, fontWeight: 600 }}>Help</div>
          {onClose && <button className="elink" onClick={onClose}>Close</button>}
        </div>
        <input className="inp" type="search" placeholder="Search help…" aria-label="Search help" value={q} onChange={(e) => setQ(e.target.value)} />
        {phone && <button className="btn2 htoggle" onClick={() => setOpenList((v) => !v)} aria-expanded={openList}>{openList ? 'Hide contents' : 'Contents'}</button>}
        <nav className={'helptoc' + (phone && !openList && !words.length ? ' hclosed' : '')}>
          {groups.map(([g, list]) => (
            <div key={g} className="htocgroup">
              <div className="htocg">{g}</div>
              {list.map((s) => <button key={s.id} className="htocitem" onClick={() => jump(s.id)}>{s.title}</button>)}
            </div>
          ))}
          {!shown.length && <div style={{ fontSize: 14, color: '#4A5561' }}>Nothing matches “{q}”. Try a shorter word.</div>}
        </nav>
        {!phone && <button className="btn2" onClick={() => window.print()}>Print this manual</button>}
      </aside>
      <div className="helpbody" ref={bodyRef}>
        <div className="printonly" style={{ fontSize: 22, fontWeight: 600 }}>MyWunderland help</div>
        {groups.map(([g, list]) => (
          <Fragment key={g}>
            <h2 className="hgroup">{g}</h2>
            {list.map((s) => (
              <section key={s.id} id={'help-' + s.id} className="hsec">
                <h3>{s.title}</h3>
                {render(s.body, jump, titleOf)}
              </section>
            ))}
          </Fragment>
        ))}
        <div className="hfoot noprint">Something missing or unclear? Tell an admin, and it will be added to this help.</div>
      </div>
    </div>
  );
}

/**
 * A small, safe text format for the manual (no HTML):
 *   blank line = new paragraph · "### " small heading · "- " bullet · "1. " numbered step · "> " tip box
 *   **bold** · [[section-id]] or [[section-id|words]] = link to another section
 */
function render(body: string, jump: (id: string) => void, titleOf: (id: string) => string | undefined): ReactNode[] {
  const out: ReactNode[] = [];
  const lines = body.replace(/^\n+|\s+$/g, '').split('\n');
  let i = 0; let k = 0;
  const inline = (t: string): ReactNode[] => {
    const parts: ReactNode[] = [];
    const re = /\*\*(.+?)\*\*|\[\[([a-z0-9-]+)(?:\|(.+?))?\]\]/g;
    let last = 0; let m: RegExpExecArray | null; let n = 0;
    while ((m = re.exec(t))) {
      if (m.index > last) parts.push(t.slice(last, m.index));
      if (m[1]) parts.push(<b key={n++}>{m[1]}</b>);
      else {
        const id = m[2]!; const label = m[3] ?? titleOf(id);
        // a link to a part this person can't see is shown as plain words
        parts.push(titleOf(id) ? <button key={n++} className="hlink" onClick={() => jump(id)}>{label}</button>
          : <Fragment key={n++}>{m[3] ?? HELP.find((x) => x.id === id)?.title ?? ''}</Fragment>);
      }
      last = re.lastIndex;
    }
    if (last < t.length) parts.push(t.slice(last));
    return parts;
  };
  while (i < lines.length) {
    const l = lines[i]!.trimEnd();
    if (!l.trim()) { i++; continue; }
    if (l.startsWith('### ')) { out.push(<h4 key={k++}>{inline(l.slice(4))}</h4>); i++; continue; }
    if (/^- /.test(l)) {
      const items: string[] = [];
      while (i < lines.length && /^(- |  \S)/.test(lines[i]!)) { if (lines[i]!.startsWith('- ')) items.push(lines[i]!.slice(2)); else items[items.length - 1] += ' ' + lines[i]!.trim(); i++; }
      out.push(<ul key={k++}>{items.map((t, j) => <li key={j}>{inline(t)}</li>)}</ul>);
      continue;
    }
    if (/^\d+\. /.test(l)) {
      const items: string[] = [];
      while (i < lines.length && /^(\d+\. |   \S)/.test(lines[i]!)) { const m = /^\d+\. (.*)$/.exec(lines[i]!); if (m) items.push(m[1]!); else items[items.length - 1] += ' ' + lines[i]!.trim(); i++; }
      out.push(<ol key={k++}>{items.map((t, j) => <li key={j}>{inline(t)}</li>)}</ol>);
      continue;
    }
    if (l.startsWith('> ')) {
      const t: string[] = [];
      while (i < lines.length && lines[i]!.startsWith('> ')) { t.push(lines[i]!.slice(2)); i++; }
      out.push(<div key={k++} className="htip">{inline(t.join(' '))}</div>);
      continue;
    }
    const p: string[] = [];
    while (i < lines.length && lines[i]!.trim() && !/^(### |- |\d+\. |> )/.test(lines[i]!)) { p.push(lines[i]!.trim()); i++; }
    out.push(<p key={k++}>{inline(p.join(' '))}</p>);
  }
  return out;
}
